/*
  File: CalendarPage.tsx
  Path: /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/CalendarPage.tsx
  Description:
    AQUORIX Calendar Tropical Money port.
    Phase 0: custom shell, tokens, read-only getDashboardCalendar adapter.
  Version: 1.0.0
  Status: ACTIVE - Calendar Port Phase 0

  Change Log:
    - 2026-07-08 - v1.0.0:
      - Replace FullCalendar page shell with Tropical Money custom calendar shell.
      - Remove FullCalendar imports from this page.
      - Add read-only getDashboardCalendar adapter scaffold.
      - Preserve display-time law: use start_time, never starts_at, for display.
*/

import React, { useEffect, useMemo, useState } from "react";
import { getDashboardCalendar } from "../../../api/dashboardSchedule";
import type { DashboardScheduleSession } from "../../../types/dashboardSchedule";
import "./CalendarPage.css";

type CalendarView = "month" | "week" | "day" | "year";
type CalendarFilter = "all" | "dive" | "course";
type YearMode = "rolling" | "calendar";
type LoadStatus = "loading" | "ready" | "error";

type CalendarItem = {
  uid: string;
  type: "dive" | "course" | string;
  title: string;
  date: string;
  time: string;
  kind: string;
  ops: string;
  cap: number;
  used: number;
  rem: number;
  priceMinor: number | null;
  cur: string;
  vessel: string | null;
  guide: string | null;
  meet: string | null;
  meetLoc: string | null;
  category: string | null;
  agency: string | null;
  level: string | null;
  sid: number | string | null;
  crid: number | string | null;
};

type DayAgg = {
  items: CalendarItem[];
  cap: number;
  used: number;
  rem: number;
  anyWait: boolean;
  dives: number;
  courses: number;
  fill: number;
};

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}

function t12(value: string): string {
  if (!value) return "";
  const [hRaw, minRaw] = value.split(":");
  const h = Number(hRaw);
  const min = minRaw || "00";
  if (!Number.isFinite(h)) return value;
  const suffix = h >= 12 ? "p" : "a";
  const hour = h % 12 || 12;
  return `${hour}:${min}${suffix}`;
}

function ymdFromDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function todayYmd(): string {
  return ymdFromDate(new Date());
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1, 12, 0, 0));
  const end = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0, 12, 0, 0));

  return {
    start: ymdFromDate(start),
    end: ymdFromDate(end),
    y: now.getFullYear(),
    m: now.getMonth(),
  };
}

function monthLabel(y: number, m: number): string {
  return `${MONTHS[m]} ${y}`;
}

function monthDayCount(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0, 12, 0, 0)).getUTCDate();
}

function shortMonthLabel(y: number, m: number, includeYear: boolean): string {
  return `${MONTHS[m].slice(0, 3)}${includeYear ? ` ’${String(y).slice(2)}` : ""}`;
}

function monthsForYear(base: { y: number; m: number }, mode: YearMode): Array<{ y: number; m: number }> {
  if (mode === "rolling") {
    return Array.from({ length: 12 }, (_, index) => {
      const rawMonth = base.m + index;
      const y = base.y + Math.floor(rawMonth / 12);
      const m = ((rawMonth % 12) + 12) % 12;
      return { y, m };
    });
  }

  return Array.from({ length: 12 }, (_, m) => ({ y: base.y, m }));
}

function yearTileClass(agg: DayAgg | null): string {
  if (!agg) return "y0";
  if (agg.anyWait) return "yw";
  if (agg.fill >= 1) return "y4";
  if (agg.fill >= 0.67) return "y3";
  if (agg.fill >= 0.34) return "y2";
  return "y1";
}

function weekStartYmd(value: string): string {
  const base = new Date(`${value}T12:00:00Z`);
  const back = (base.getUTCDay() + 6) % 7;
  base.setUTCDate(base.getUTCDate() - back);
  return ymdFromDate(base);
}

function addDaysYmd(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return ymdFromDate(date);
}

function dayNumber(value: string): number {
  return Number(value.slice(8, 10));
}

function weekdayName(value: string): string {
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const date = new Date(`${value}T12:00:00Z`);
  return labels[(date.getUTCDay() + 6) % 7];
}

function railTime(hour: number): string {
  const suffix = hour >= 12 ? "p" : "a";
  const display = hour % 12 || 12;
  return `${display}:00${suffix}`;
}

function weekRangeLabel(start: string): string {
  const end = addDaysYmd(start, 6);
  const startDate = new Date(`${start}T12:00:00Z`);
  const endDate = new Date(`${end}T12:00:00Z`);
  return `${MONTHS[startDate.getUTCMonth()]} ${startDate.getUTCDate()} – ${MONTHS[endDate.getUTCMonth()]} ${endDate.getUTCDate()}`;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapSessionToCalendarItem(session: DashboardScheduleSession): CalendarItem | null {
  const date = session.session_date;
  if (!date) return null;

  const pointer = session.inventory_pointer || {};
  const metadata = session.metadata || {};
  const type = session.type || (session.course_run_id ? "course" : "dive");
  const title = session.title || session.site_name || "Session";
  const cap = Number(session.capacity_total ?? 0);
  const used = Number(session.capacity_consumed ?? 0);
  const rem = Number(session.capacity_remaining ?? Math.max(0, cap - used));

  return {
    uid: String(session.unified_id || session.id || session.session_id || session.course_run_id || `${type}:${date}:${session.start_time}`),
    type,
    title,
    date,
    time: session.start_time || "",
    kind: session.session_type || type,
    ops: session.ops_status || session.session_status || "—",
    cap,
    used,
    rem,
    priceMinor: toNumberOrNull(session.price_from_minor),
    cur: session.currency || "JOD",
    vessel: (metadata as any).vessel_name || session.vessel_name || null,
    guide: (metadata as any).lead_guide_name || null,
    meet: session.meet_time || null,
    meetLoc: session.meet_location || null,
    category: (metadata as any).category || null,
    agency: (metadata as any).agency || null,
    level: (metadata as any).level || null,
    sid: pointer.session_id || session.session_id || null,
    crid: pointer.course_run_id || session.course_run_id || null,
  };
}

export default function CalendarPage() {
  const initialRange = useMemo(() => currentMonthRange(), []);
  const [view, setView] = useState<CalendarView>("month");
  const [filter, setFilter] = useState<CalendarFilter>("all");
  const [yearMode, setYearMode] = useState<YearMode>("rolling");
  const [cursor, setCursor] = useState({ y: initialRange.y, m: initialRange.m });
  const [weekAnchor, setWeekAnchor] = useState(todayYmd());
  const [range, setRange] = useState({ start: initialRange.start, end: initialRange.end });
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [scheduleData, setScheduleData] = useState<any>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCalendar() {
      setStatus("loading");

      try {
        const json = await getDashboardCalendar(range.start, range.end);

        if (!cancelled) {
          setScheduleData(json);
          setStatus("ready");
        }
      } catch (err) {
        console.error("[CalendarPage] Calendar load failed:", err);

        if (!cancelled) {
          setStatus("error");
        }
      }
    }

    void loadCalendar();

    return () => {
      cancelled = true;
    };
  }, [range.start, range.end]);

  const items = useMemo(() => {
    const sessions = Array.isArray(scheduleData?.sessions) ? scheduleData.sessions : [];

    return sessions
      .map((session: DashboardScheduleSession) => mapSessionToCalendarItem(session))
      .filter((item: CalendarItem | null): item is CalendarItem => Boolean(item));
  }, [scheduleData]);

  const visibleItems = useMemo(() => {
    return items.filter((item: CalendarItem) => filter === "all" || item.type === filter);
  }, [items, filter]);

  const byDate = (date: string): CalendarItem[] => {
    return visibleItems.filter((item: CalendarItem) => item.date === date);
  };

  const dayAgg = (date: string): DayAgg | null => {
    const dayItems = byDate(date);
    if (!dayItems.length) return null;

    const cap = dayItems.reduce((a, x) => a + x.cap, 0);
    const used = dayItems.reduce((a, x) => a + x.used, 0);
    const anyWait = dayItems.some((x) => x.rem <= 0);
    const dives = dayItems.filter((x) => x.type === "dive").length;
    const courses = dayItems.filter((x) => x.type === "course").length;

    return {
      items: dayItems,
      cap,
      used,
      rem: cap - used,
      anyWait,
      dives,
      courses,
      fill: cap ? used / cap : 0,
    };
  };

  const whisperClass = (agg: DayAgg): string => {
    if (agg.anyWait) return "w-wait";
    if (agg.fill >= 0.9) return "w-busy";
    if (agg.fill >= 0.5) return "w-mid";
    return "w-open";
  };

  const renderMonth = () => {
    const { y, m } = cursor;
    const first = new Date(Date.UTC(y, m, 1));
    const lead = (first.getUTCDay() + 6) % 7;
    const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const prevDays = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const total = Math.ceil((lead + days) / 7) * 7;
    const cells = [];

    for (let i = 0; i < total; i += 1) {
      const dayNum = i - lead + 1;
      const inMonth = dayNum >= 1 && dayNum <= days;
      let cy = y;
      let cm = m;
      let cd = dayNum;

      if (dayNum < 1) {
        cm = m - 1;
        cd = prevDays + dayNum;
        if (cm < 0) {
          cm = 11;
          cy -= 1;
        }
      } else if (dayNum > days) {
        cm = m + 1;
        cd = dayNum - days;
        if (cm > 11) {
          cm = 0;
          cy += 1;
        }
      }

      const dISO = iso(cy, cm, cd);

      if (!inMonth) {
        cells.push(
          <div key={`out-${dISO}-${i}`} className="cell out">
            <div className="cnum-row"><span className="dnum">{cd}</span></div>
          </div>
        );
        continue;
      }

      const agg = dayAgg(dISO);
      const className = [
        "cell",
        dISO === todayYmd() ? "today" : "",
        dISO === selectedDay ? "sel" : "",
      ].filter(Boolean).join(" ");

      cells.push(
        <div key={dISO} className={className} onClick={() => { setSelectedDay(dISO); setSelectedItem(null); }}>
          <div className="cnum-row"><span className="dnum">{cd}</span></div>

          {agg ? (
            <>
              <div className={`whisper ${whisperClass(agg)}`}>
                <i style={{ width: `${Math.max(6, Math.round(agg.fill * 100))}%` }} />
              </div>

              <div className="chips">
                {agg.items.slice(0, 3).map((item) => (
                  <div
                    key={item.uid}
                    className={`chip ${item.type === "course" ? "course" : ""} ${selectedItem === item.uid ? "sel" : ""}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedDay(dISO);
                      setSelectedItem(item.uid);
                    }}
                  >
                    <span className="t">{t12(item.time)}</span>
                    <span className="n">{item.title}</span>
                    {item.rem <= 0 ? <span className="wl" /> : null}
                  </div>
                ))}
              </div>

              {agg.items.length > 3 ? (
                <div
                  className="more"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedDay(dISO);
                    setSelectedItem(null);
                  }}
                >
                  +{agg.items.length - 3} more
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      );
    }

    return (
      <>
        <div className="dow-row">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="dow">{day}</div>
          ))}
        </div>
        <div className="grid">{cells}</div>
      </>
    );
  };

  const renderWeek = () => {
    const weekStart = weekStartYmd(weekAnchor);
    const dates = Array.from({ length: 7 }, (_, index) => addDaysYmd(weekStart, index));

    let startH = 8;
    let endH = 18;

    visibleItems.forEach((item: CalendarItem) => {
      if (!dates.includes(item.date)) return;
      const h = Number(item.time.slice(0, 2));
      if (!Number.isFinite(h)) return;
      if (h < startH) startH = h;
      if (h + 1 > endH) endH = h + 1;
    });

    const hours = Array.from({ length: endH - startH + 1 }, (_, index) => startH + index);

    return (
      <div className="week">
        <div className="week-head">
          <div className="wh" />
          {dates.map((date) => (
            <div key={date} className={`wh ${date === todayYmd() ? "today" : ""}`}>
              <div className="wd">{weekdayName(date)}</div>
              <div className="wn">{dayNumber(date)}</div>
            </div>
          ))}
        </div>

        <div className="week-grid">
          <div className="rail">
            {hours.map((hour) => (
              <div key={hour} className="hr">{railTime(hour)}</div>
            ))}
          </div>

          {dates.map((date) => {
            const dayItems = byDate(date);

            return (
              <div
                key={date}
                className="wcol"
                onClick={() => {
                  setSelectedDay(date);
                  setSelectedItem(null);
                }}
              >
                {hours.map((hour) => (
                  <div key={`${date}-${hour}`} className="slot" />
                ))}

                {dayItems.map((item) => {
                  const [hRaw, mRaw] = item.time.split(":");
                  const h = Number(hRaw);
                  const min = Number(mRaw || 0);
                  const top = ((Number.isFinite(h) ? h : startH) - startH) * 44 + (Number.isFinite(min) ? min : 0) / 60 * 44;

                  return (
                    <div
                      key={item.uid}
                      className={`wev ${item.type === "course" ? "course" : ""}`}
                      style={{
                        top: `${top}px`,
                        height: "40px",
                        boxShadow: selectedItem === item.uid ? "0 0 0 2px rgba(22,179,199,.35)" : undefined,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedDay(date);
                        setSelectedItem(item.uid);
                      }}
                    >
                      <div className="wt">{t12(item.time)}</div>
                      <div className="wn2">{item.title}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

    const renderYear = () => {
    const months = monthsForYear(cursor, yearMode);
    const rolling = yearMode === "rolling";

    return (
      <div className="year-wrap">
        <div className="ygrid">
          {months.map(({ y, m }) => {
            const first = new Date(Date.UTC(y, m, 1));
            const lead = (first.getUTCDay() + 6) % 7;
            const days = monthDayCount(y, m);
            let monthSessions = 0;

            return (
              <div key={`${y}-${m}`} className={`ymonth ${y === cursor.y && m === cursor.m ? "ym-now" : ""}`}>
                <div className="ymonth-head">
                  <span className="ym-name">{shortMonthLabel(y, m, rolling)}</span>
                  {Array.from({ length: days }, (_, index) => {
                    const date = iso(y, m, index + 1);
                    const agg = dayAgg(date);
                    monthSessions += agg ? agg.items.length : 0;
                    return null;
                  })}
                  {monthSessions ? <span className="ym-count">{monthSessions}</span> : null}
                </div>

                <div className="ytile-grid">
                  {Array.from({ length: lead }, (_, index) => (
                    <span key={`lead-${y}-${m}-${index}`} className="ytile ye" />
                  ))}

                  {Array.from({ length: days }, (_, index) => {
                    const d = index + 1;
                    const date = iso(y, m, d);
                    const agg = dayAgg(date);
                    const cls = yearTileClass(agg);
                    const tip = agg
                      ? `${MONTHS[m]} ${d} · ${agg.items.length} ${agg.items.length === 1 ? "session" : "sessions"} · ${agg.used}/${agg.cap} booked (${Math.round(agg.fill * 100)}%)`
                      : `${MONTHS[m]} ${d} · no dives`;

                    return (
                      <span
                        key={date}
                        className={`ytile ${cls}`}
                        title={tip}
                        onClick={agg ? () => pickYearDay(date) : undefined}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="ylegend">
          <span>Quiet</span>
          <span className="ytile y0" />
          <span style={{ marginLeft: "9px" }}>Open</span>
          <span className="ytile y1" />
          <span className="ytile y2" />
          <span className="ytile y3" />
          <span className="ytile y4" />
          <span>Full</span>
          <span className="ytile yw" style={{ marginLeft: "12px" }} />
          <span>Waitlist</span>
        </div>

        <div className="ycap">
          Each tile is one day. Still water is quiet; open days lighten to reef-green; a day fills toward jewel-teal; and a full day blooms deep teal kissed with gold — the catch harvested. A sold-out day turns violet. Hover for the count and percent; click to open it. The prototype carries the real July slice, so only July shows density here; production paints the full {yearMode === "rolling" ? "rolling twelve-month" : "calendar-year"} range.
        </div>
      </div>
    );
  };

    const renderDay = () => {
    const date = selectedDay || todayYmd();
    const dt = new Date(`${date}T12:00:00Z`);
    const label = `${weekdayName(date)}, ${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
    const itemsForDay = byDate(date);

    return (
      <div className="dayview">
        <div className="dv-h">{label}</div>
        {itemsForDay.length
          ? itemsForDay.map((item: CalendarItem) => rowHTML(item))
          : <div className="insp-empty">No dives posted. A quiet day.</div>}
      </div>
    );
  };

  const findItem = (uid: string | null): CalendarItem | null => {
    if (!uid) return null;
    return visibleItems.find((item: CalendarItem) => item.uid === uid) || null;
  };

    const pickYearDay = (date: string) => {
    const dt = new Date(`${date}T12:00:00Z`);

    setCursor({
      y: dt.getUTCFullYear(),
      m: dt.getUTCMonth(),
    });
    setRange({
      start: iso(dt.getUTCFullYear(), dt.getUTCMonth(), 1),
      end: iso(dt.getUTCFullYear(), dt.getUTCMonth(), monthDayCount(dt.getUTCFullYear(), dt.getUTCMonth())),
    });
    setSelectedDay(date);
    setSelectedItem(null);
    setView("month");
  };

  const weekStartForTitle = weekStartYmd(weekAnchor);
  const title =
    view === "week"
      ? weekRangeLabel(weekStartForTitle)
      : view === "year"
        ? (() => {
            const months = monthsForYear(cursor, yearMode);
            const first = months[0];
            const last = months[11];
            return yearMode === "rolling"
              ? `${shortMonthLabel(first.y, first.m, false)} ${first.y} – ${shortMonthLabel(last.y, last.m, false)} ${last.y}`
              : `${first.y}`;
          })()
        : monthLabel(cursor.y, cursor.m);

  const shiftPeriod = (dir: number) => {
    if (view === "week") {
      const nextAnchor = addDaysYmd(weekAnchor, dir * 7);
      const nextStart = weekStartYmd(nextAnchor);
      const nextEnd = addDaysYmd(nextStart, 6);

      setWeekAnchor(nextAnchor);
      setRange({ start: nextStart, end: nextEnd });
      setSelectedDay(null);
      setSelectedItem(null);
      return;
    }

    if (view !== "month") return;

    const next = { ...cursor, m: cursor.m + dir };

    if (next.m < 0) {
      next.m = 11;
      next.y -= 1;
    }

    if (next.m > 11) {
      next.m = 0;
      next.y += 1;
    }

    const start = new Date(Date.UTC(next.y, next.m, 1, 12, 0, 0));
    const end = new Date(Date.UTC(next.y, next.m + 1, 0, 12, 0, 0));

    setCursor(next);
    setRange({ start: ymdFromDate(start), end: ymdFromDate(end) });
    setSelectedDay(null);
    setSelectedItem(null);
  };

  const goToday = () => {
    const next = currentMonthRange();
    const today = todayYmd();

    setCursor({ y: next.y, m: next.m });
    setWeekAnchor(today);

    if (view === "week") {
      const nextStart = weekStartYmd(today);
      setRange({ start: nextStart, end: addDaysYmd(nextStart, 6) });
    } else {
      setRange({ start: next.start, end: next.end });
    }

    if (view === "day") setSelectedDay(today);
    setSelectedItem(null);
  };

  const money = (minor: number | null, cur: string): string => {
    if (minor === null || minor === undefined) return "TBD";
    const div = String(cur || "JOD").toUpperCase() === "JOD" ? 1000 : 100;
    return `${String(cur || "JOD").toUpperCase()} ${(minor / div).toFixed(2)}`;
  };

  const routeToItem = (item: CalendarItem) => {
    if (item.type === "course" && item.crid) {
      window.location.href = `/dashboard/courses?course_run_id=${item.crid}`;
      return;
    }

    if (item.sid) {
      window.location.href = `/dashboard/ops-board?session_id=${item.sid}`;
    }
  };

  const seatChip = (item: CalendarItem) => {
    if (item.rem <= 0) return <span className="seatchip wait">Full &middot; waitlist</span>;
    return <span className="seatchip open">{item.rem} open</span>;
  };

  const rowHTML = (item: CalendarItem) => {
    const selectedStyle = selectedItem === item.uid ? { background: "rgba(255,255,255,.7)" } : undefined;

    return (
      <div
        key={item.uid}
        className="irow"
        style={selectedStyle}
        onClick={() => {
          setSelectedDay(item.date);
          setSelectedItem(item.uid);
        }}
      >
        <span className={`pill ${item.type}`}>{item.type}</span>
        <span className="itime">{t12(item.time)}</span>
        <span className="ittl">{item.title}</span>
        {seatChip(item)}
        <span className="view">View &rarr;</span>
      </div>
    );
  };

  const dayCrown = (date: string) => {
    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dt = new Date(`${date}T12:00:00Z`);
    const label = `${weekdays[(dt.getUTCDay() + 6) % 7]}, ${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
    const agg = dayAgg(date);

    let summary = "No dives posted.";
    if (agg) {
      const parts = [];
      if (agg.dives) parts.push(`${agg.dives} ${agg.dives === 1 ? "dive" : "dives"}`);
      if (agg.courses) parts.push(`${agg.courses} ${agg.courses === 1 ? "course" : "courses"}`);
      summary = `${parts.join(" · ")} · ${agg.used} of ${agg.cap} seats booked`;
    }

    return (
      <div className="insp-crown">
        <div>
          <div className="dc-date">{label}</div>
          <div className="dc-sum">{summary}</div>
        </div>
        <button
          className="dc-close"
          type="button"
          onClick={() => {
            setSelectedDay(null);
            setSelectedItem(null);
          }}
        >
          Close
        </button>
      </div>
    );
  };

  const expandedHTML = (item: CalendarItem) => {
    const pct = item.cap ? Math.round((item.used / item.cap) * 100) : 0;
    const barColor = item.rem <= 0 ? "var(--violet)" : (pct >= 90 ? "var(--turq-deep)" : (pct >= 50 ? "var(--turq)" : "var(--reef)"));

    const rows: Array<[string, string]> = [];
    rows.push(["Type", item.type === "course" ? `Course · ${[item.agency, item.level].filter(Boolean).join(" ")}`.trim() : `Dive · ${item.kind === "boat" ? "Boat" : "Shore"}`]);
    rows.push(["When", `${t12(item.time)}${item.meet ? ` · meet ${t12(item.meet)}` : ""}`]);
    rows.push(["Status", item.ops || "—"]);

    if (item.type === "dive") {
      rows.push(["Vessel", item.vessel || "—"]);
      rows.push(["Guide", item.guide || "—"]);
      if (item.category) rows.push(["Category", item.category]);
    }

    if (item.meetLoc) rows.push(["Meet point", item.meetLoc]);

    const routeLabel = item.type === "course" ? "View course run" : "Open in Ops Board";

    return (
      <div className="iexp">
        <div className="iexp-top">
          <div><span className={`pill ${item.type}`}>{item.type}</span></div>
          <div className="ttl">{item.title}</div>
          <div className="price">
            <div className="amt">{money(item.priceMinor, item.cur)}</div>
            <div className="per">{item.type === "course" ? "per student" : "per diver"}</div>
          </div>
        </div>

        <div className="igrid">
          {rows.map(([key, value]) => (
            <div className="ifield" key={key}>
              <div className="k">{key}</div>
              <div className="v">{value}</div>
            </div>
          ))}

          <div className="ifield" style={{ gridColumn: "1/-1" }}>
            <div className="k">Capacity</div>
            <div className="cap-line">
              <div className="v">{item.used} / {item.cap} booked &middot; {item.rem} remaining</div>
              <div className="cap-bar">
                <i style={{ width: `${Math.max(4, pct)}%`, background: barColor }} />
              </div>
            </div>
          </div>
        </div>

        <div className="iexp-act">
          <button
            className={item.type === "course" ? "course" : ""}
            type="button"
            onClick={() => routeToItem(item)}
          >
            {routeLabel} &rarr;
          </button>
        </div>
      </div>
    );
  };

  const renderInspector = () => {
    if (!selectedDay) {
      return <div className="insp-empty">Select a day or a dive to see the details.</div>;
    }

    const itemsForDay = byDate(selectedDay);
    const item = findItem(selectedItem);
    const others = selectedItem ? itemsForDay.filter((candidate: CalendarItem) => candidate.uid !== selectedItem) : [];

    return (
      <>
        {dayCrown(selectedDay)}
        <div className="insp-body">
          {selectedItem ? (
            <>
              {item ? expandedHTML(item) : null}
              {others.length ? (
                <>
                  <div className="others-label">Also this day</div>
                  {others.map((candidate: CalendarItem) => rowHTML(candidate))}
                </>
              ) : null}
            </>
          ) : (
            itemsForDay.length
              ? itemsForDay.map((candidate: CalendarItem) => rowHTML(candidate))
              : <div className="insp-empty">No dives posted. A quiet day.</div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="aqx-calendar-page aqx-cal">
      <div className="aqx-calendar-wrap"> 

        <div className="aqx-calendar-card">
          <div className="aqx-calendar-toolbar">
            <div className="aqx-calendar-nav">
              <button type="button" aria-label="Previous" onClick={() => shiftPeriod(-1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="aqx-calendar-today-btn" type="button" onClick={goToday}>Today</button>
              <button type="button" aria-label="Next" onClick={() => shiftPeriod(1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="aqx-calendar-month-title">{title}</div>

            <div className="aqx-calendar-seg year-mode" style={{ display: view === "year" ? "inline-flex" : "none" }}>
              <button
                className={yearMode === "rolling" ? "on" : ""}
                data-ym="rolling"
                type="button"
                onClick={() => setYearMode("rolling")}
              >
                Rolling 12
              </button>
              <button
                className={yearMode === "calendar" ? "on" : ""}
                data-ym="calendar"
                type="button"
                onClick={() => setYearMode("calendar")}
              >
                Calendar year
              </button>
            </div>

            <div className="aqx-calendar-grow" />

            <div className="aqx-calendar-seg type">
              <button className={filter === "all" ? "on" : ""} data-t="all" type="button" onClick={() => setFilter("all")}>All</button>
              <button className={filter === "dive" ? "on" : ""} data-t="dive" type="button" onClick={() => setFilter("dive")}>Dives</button>
              <button className={filter === "course" ? "on" : ""} data-t="course" type="button" onClick={() => setFilter("course")}>Courses</button>
            </div>

            <div className="aqx-calendar-seg view">
              <button className={view === "month" ? "on" : ""} data-v="month" type="button" onClick={() => setView("month")}>Month</button>
              <button className={view === "week" ? "on" : ""} data-v="week" type="button" onClick={() => setView("week")}>Week</button>
              <button className={view === "day" ? "on" : ""} data-v="day" type="button" onClick={() => setView("day")}>Day</button>
              <button className={view === "year" ? "on" : ""} data-v="year" type="button" onClick={() => setView("year")}>Year</button>
            </div>
          </div>

          <div className="aqx-calendar-surface">
            {status === "error" ? (
              <div className="insp-empty">
                Calendar load failed.
              </div>
            ) : null}

            {view === "month" ? renderMonth() : null}
            {view === "week" ? renderWeek() : null}
            {view === "year" ? renderYear() : null}
            {view === "day" ? renderDay() : null}
          </div>
        </div>

        <div className="inspector" id="inspector">
          {renderInspector()}
        </div>
      </div>
    </div>
  );
}
